import React, { useMemo } from "react";
import { isEmpty, isEqual } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Select from 'react-select';
import DateInput from "./DateInput";

const getDateValue = (type) => {
    const date = new Date()
    switch(type){
        case 'tomorrow':{
            return {min: new Date(moment(date).add(1,'days').startOf('day')), max: new Date(moment(date).add(1,'days').endOf('day'))};
        }
        case 'today': {
            return {min: new Date(moment(date).startOf('day')), max: new Date(moment(date).endOf('day'))};
        }
        case 'yesterday':{
            return {min: new Date(moment(date).subtract(1,'days').startOf('day')), max: new Date(moment(date).subtract(1,'days').endOf('day'))};
        }
        case 'prevWeek':{
            const prevWeek = moment(date).subtract(1, 'week');
            return {min: new Date(prevWeek.startOf('isoWeek')), max: new Date(prevWeek.endOf('isoWeek'))};
        }
        case 'week':{
            return {min: new Date(moment(date).startOf('isoWeek')), max: new Date(moment(date).endOf('isoWeek')), periodType: 'week'};
        }
        case 'prevMonth':{
            const prevMonth = moment(date).subtract(1, 'month');
            return {min: new Date(prevMonth.startOf('month')), max: new Date(prevMonth.endOf('month'))};
        }
        case 'month':{
            return {min: new Date(moment(date).startOf('month')), max: new Date(moment(date).endOf('month')), periodType: 'month'};
        }
        case 'year':{
            return {min: new Date(moment(date).startOf('year')), max: new Date(moment(date).endOf('year'))};
        }
        case 'prevYear': {
            const prevYearDate = moment(date).subtract(1, 'year');
            return {min: new Date(moment(prevYearDate).startOf('year')), max: new Date(moment(prevYearDate).endOf('year'))};
        }
        default: {
            return {min: date, max: date};
        }
    }
}

export default function DateSelect({optionsDate = null, limit = null, onChange, width = 250, isLoading = false, isDisabled = false, selected}) {
    const [ selectedDate, setSelectedDate ] = useState(null);
    const [ openDateInput, setOpenDateInput ] = useState(false);
    const [ dateInputValue, setDateInputValue ] = useState({
        min: new Date(moment().startOf('month')),
        max: new Date(moment().endOf('month')),
    })

    const baseDateOptions = useMemo(() => [
        {label: `Завтра ${moment(getDateValue('tomorrow').min).format('DD.MM.YYYY')}`, value: getDateValue('tomorrow')},
        {label: `Сегодня ${moment(getDateValue('today').min).format('DD.MM.YYYY')}`, value: getDateValue('today')},
        {label: `Вчера ${moment(getDateValue('yesterday').min).format('DD.MM.YYYY')}`, value: getDateValue('yesterday')},
        {label: 'Прошлая неделя', value: getDateValue('prevWeek')},
        {label: 'Текущая неделя', value: getDateValue('week')},
        {label: 'Прошлый месяц', value: getDateValue('prevMonth')},
        {label: 'Текущий месяц', value: getDateValue('month')},
        {label: `${moment(getDateValue('year').min).year()} год`, value: getDateValue('year')},
        {label: `${moment(getDateValue('prevYear').min).year()} год`, value: getDateValue('prevYear')},
        {label: 'Период', value: 'period'},
    ], []);

    const [dateValue, setDateValue] = useState(baseDateOptions);

    useEffect(() => {
        const todayOption = baseDateOptions.find((item) => item.label.includes('Сегодня') || item.value === getDateValue('today'));
        if (todayOption && !selected) {
            setSelectedDate(todayOption);
            onChange(todayOption.value);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selected && selected.value) {
            const entityOption = baseDateOptions.find((item) => isEqual(selected.value, item.value));
            if (entityOption && (!selectedDate || !isEqual(entityOption.value, selectedDate.value))) setSelectedDate(entityOption);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseDateOptions, selected]);

    useEffect(() => {
        if (!isEmpty(optionsDate)) {
            const { prevWeek, prevMonth, prevYear, tomorrow, period, noYear } = optionsDate;
        
            const getTargetRange = (type) => {
                switch (type) {
                    case 'prevWeek': return getDateValue('prevWeek');
                    case 'prevMonth': return getDateValue('prevMonth');
                    case 'prevYear': return getDateValue('prevYear');
                    case 'year': return getDateValue('year');
                    case 'tomorrow': {
                        const tomorrowDate = moment().add(1, 'day').startOf('day');
                        return {
                            min: tomorrowDate.toDate(),
                            max: tomorrowDate.clone().endOf('day').toDate()
                        };
                    }
                    default: return null;
                }
            };
        
            const isRange = (date1, date2) => {
                if (!date1 || !date2) return false;
                return date1.min.getTime() === date2.min.getTime() && date1.max.getTime() === date2.max.getTime();
            };
        
            let updated = [...baseDateOptions];
        
            if (!prevYear) {
                const target = getTargetRange('prevYear');
                updated = updated.filter(item => {
                    if (typeof item.value === 'string') return true;
                    return !isRange(item.value, target);
                });
            }

            if (noYear) {
                const target = getTargetRange('year');
                updated = updated.filter(item => {
                    if (typeof item.value === 'string') return true;
                    return !isRange(item.value, target);
                });
            }
        
            if (!prevMonth) {
                const target = getTargetRange('prevMonth');
                updated = updated.filter(item => {
                    if (typeof item.value === 'string') return true;
                    return !isRange(item.value, target);
                });
            }
        
            if (!prevWeek) {
                const target = getTargetRange('prevWeek');
                updated = updated.filter(item => {
                    if (typeof item.value === 'string') return true;
                    return !isRange(item.value, target);
                });
            }
        
            if (!tomorrow) {
                const target = getTargetRange('tomorrow');
                updated = updated.filter(item => {
                    if (typeof item.value === 'string') return true;
                    return !isRange(item.value, target);
                });
            }
        
            if (!period) updated = updated.filter(item => item.value !== 'period');
        
            setDateValue(updated);
        
            if (selectedDate && !selectedDate.notCheck && !updated.some((opt) => JSON.stringify(opt.value) === JSON.stringify(selectedDate.value))) {
                const newSelected = updated[0] || null;
                setSelectedDate(newSelected);
                if (newSelected && newSelected.value !== 'period') onChange(newSelected.value);
            }
        } else {
            setDateValue(baseDateOptions);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionsDate]);

    const onChangeDateSelect = (e) => {
        if(e.value !== 'period') {
            setSelectedDate(e);
            return onChange(e.value);
        }
        setOpenDateInput(true);
    };

    const onCloseDateInput = () => setOpenDateInput(false);

    const onSubmitDateInput = () => {
        if (optionsDate.period && dateInputValue && dateInputValue.min && dateInputValue.max) {
            let updated = [...dateValue];
            const period = updated.find((item) => item.value === 'period');
            if (period) period.label = `Период ${moment(dateInputValue.min).format('DD.MM.YYYY')} - ${moment(dateInputValue.max).format('DD.MM.YYYY')}`;
            setSelectedDate({ label: period.label, value: {min: dateInputValue.min, max: dateInputValue.max}, notCheck: true });
            onChange({min: dateInputValue.min, max: dateInputValue.max});
        }
        setOpenDateInput(false);
    }

    const onChangeDateInput = (field) => (date) => {
        const updated = { ...dateInputValue, [field]: date,};

        const isAfter = moment(updated.min).isAfter(updated.max)

        if (field === 'max' && isAfter) updated.min = date;
        if (field === 'min' && isAfter) updated.max = new Date(moment(date).endOf('day'));

        if (limit && limit.field && limit.value) {
            let diff = moment(updated.max).diff(moment(updated.min), limit.field);
            if (diff && diff > limit.value) updated.max = new Date(moment(updated.min).add(limit.value, limit.field).endOf('day'));
        }

        setDateInputValue(updated);
    }

    return(
        <div style={{ width }}>
            <Select
                isLoading={isLoading}
                isDisabled={isDisabled}
                placeholder='Дата'
                value={selectedDate}
                onChange={onChangeDateSelect}
                options={dateValue}
            />
            {openDateInput && <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', zIndex: '1', backgroundColor: '#fff', borderRadius: '4px', padding: '10px', color: '#000', marginTop: '10px' }}>
                    <div>Период с</div>
                    <DateInput                                            
                        placeholderText="Дата от"
                        selected={dateInputValue.min}
                        onChange={onChangeDateInput('min')}
                    />
                    <div>по</div>
                    <DateInput
                        placeholderText="Дата до"
                        selected={dateInputValue.max}
                        onChange={onChangeDateInput('max')}
                        maxDate={() => {
                            if (limit && limit.field && limit.value) return new Date(moment(dateInputValue.min).add(limit.value, limit.field));
                            return null;
                        }}
                        required={dateInputValue.min}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px'}}>
                        <button
                        className='btn btn-secondary'
                        onClick={onCloseDateInput}
                        >
                            Закрыть
                        </button>
                        <button 
                        className='btn btn-primary'
                        onClick={onSubmitDateInput}
                        >
                            Ок
                        </button>
                    </div>
                </div>
            </div>}
        </div>
    );
}